// protocol/guard.ts — minimal runtime validation (no dependencies)
import type { CatalogId, Rot } from '@whiskerton/content';
import { parseSpace } from '../spaces';
import type { TileXY, Vec2 } from '../types/geom';
import type { IdPrefix } from '../types/ids';
import type { SpaceId } from '../types/space';

export const INVALID: unique symbol = Symbol('invalid');
export type Guard<T> = (v: unknown) => T | typeof INVALID;
export const MAX_TEXT = 1000;          // structural cap; gameplay caps (140 chat, 500 letters) are checked by handlers from tuning

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const CROCK16 = '[0-9A-HJKMNP-TV-Z]{16}';
const CATALOG_ID = /^[a-z]+\.[a-z0-9_]+$/;
const TOKEN = /^[A-Za-z0-9_-]{1,64}$/;

function obj<S extends Record<string, Guard<unknown>>>(shape: S): Guard<{ [K in keyof S]: Exclude<ReturnType<S[K]>, typeof INVALID> }> {
  return (v) => {
    if (!isObj(v)) return INVALID;
    for (const k of Object.keys(v)) if (!(k in shape)) return INVALID;
    const out: Record<string, unknown> = {};
    for (const [k, guard] of Object.entries(shape)) {
      const r = guard(v[k]);
      if (r === INVALID) return INVALID;
      if (r !== undefined || k in v) out[k] = r;
    }
    return out as { [K in keyof S]: Exclude<ReturnType<S[K]>, typeof INVALID> };
  };
}
const int = (min: number, max: number): Guard<number> => (v) => (typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max ? v : INVALID);
const num = (min: number, max: number): Guard<number> => (v) => (typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : INVALID);

export const g: {
  str(min: number, max: number): Guard<string>;
  int(min: number, max: number): Guard<number>;
  num(min: number, max: number): Guard<number>;                 // finite only
  bool: Guard<boolean>;
  lit<T extends string | number>(...values: readonly T[]): Guard<T>;
  id<T extends string>(prefix: IdPrefix): Guard<T>;            // /^<p>_[0-9A-HJKMNP-TV-Z]{16}$/
  catalogId: Guard<CatalogId>;                                  // /^[a-z]+\.[a-z0-9_]+$/
  token: Guard<string>;                                         // /^[A-Za-z0-9_-]{1,64}$/ (shadow/spot/convo ids)
  tile: Guard<TileXY>;                                          // ints 0 … 255
  vec2: Guard<Vec2>;                                            // finite −1 … 256
  rot: Guard<Rot>;
  space: Guard<SpaceId>;                                        // parseSpace(v) !== null
  nullable<T>(inner: Guard<T>): Guard<T | null>;
  opt<T>(inner: Guard<T>): Guard<T | undefined>;                // key may be absent
  arr<T>(inner: Guard<T>, max: number): Guard<T[]>;
  obj<S extends Record<string, Guard<unknown>>>(shape: S): Guard<{ [K in keyof S]: Exclude<ReturnType<S[K]>, typeof INVALID> }>; // rejects unknown keys
} = {
  str: (min, max) => (v) => (typeof v === 'string' && v.length >= min && v.length <= max ? v : INVALID),
  int,
  num,
  bool: (v) => (typeof v === 'boolean' ? v : INVALID),
  lit: <T extends string | number>(...values: readonly T[]): Guard<T> => (v) => ((values as readonly unknown[]).includes(v) ? (v as T) : INVALID),
  id: <T extends string>(prefix: IdPrefix): Guard<T> => {
    const re = new RegExp(`^${prefix}_${CROCK16}$`);
    return (v) => (typeof v === 'string' && re.test(v) ? (v as T) : INVALID);
  },
  catalogId: (v) => (typeof v === 'string' && CATALOG_ID.test(v) ? v : INVALID),
  token: (v) => (typeof v === 'string' && TOKEN.test(v) ? v : INVALID),
  tile: obj({ x: int(0, 255), y: int(0, 255) }),
  vec2: obj({ x: num(-1, 256), y: num(-1, 256) }),
  rot: (v) => (v === 0 || v === 1 || v === 2 || v === 3 ? v : INVALID),
  space: (v) => (typeof v === 'string' && parseSpace(v) !== null ? (v as SpaceId) : INVALID),
  nullable: <T>(inner: Guard<T>): Guard<T | null> => (v) => (v === null ? null : inner(v)),
  opt: <T>(inner: Guard<T>): Guard<T | undefined> => (v) => (v === undefined ? undefined : inner(v)),
  arr: <T>(inner: Guard<T>, max: number): Guard<T[]> => (v) => {
    if (!Array.isArray(v) || v.length > max) return INVALID;
    const out: T[] = [];
    for (const item of v) { const r = inner(item); if (r === INVALID) return INVALID; out.push(r); }
    return out;
  },
  obj,
};
